/**
 * PublicSign — the PUBLIC patient-facing records-release page at /sign/:token
 * (Phase E.2). Texted to contacts. No auth, no CRM shell — the unguessable
 * per-request token (with expiry) is the only credential.
 *
 * The patient reviews/edits their prefilled info, names the doctor's office(s)
 * the records should come FROM (we don't know them up front), optionally adds a
 * pharmacy, reviews the authorization, then types their legal name + draws a
 * signature + checks the e-consent. On submit the server builds the release PDF
 * from these fields and faxes it to each office (return fax = our fax line).
 *
 * NOTE: there is deliberately NO PDF viewer here. The old page fed react-pdf a
 * presigned S3 URL, which the PHI bucket (no browser CORS) silently blocked ->
 * "temporarily unavailable". Rendering the authorization as text removes that
 * failure mode entirely; the official PDF is generated server-side at signing.
 *
 * Canvas: DPR-scaled backing store drawn via pointer events with
 * setPointerCapture + touch-action:none; EXPORT goes through an offscreen
 * 600x200 canvas so the payload stays ~10-30KB (under the server's 80KB cap).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface PatientFields {
  first_name?: string;
  last_name?: string;
  dob?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
}
interface Office { name: string; city_state: string; fax: string; }
interface ReleaseTo { name: string; address: string; phone: string; }

interface SignState {
  signed?: boolean;
  first_name?: string | null;
  prefill?: PatientFields;
  offices?: Office[];
  hospital?: Office | null;
  pharmacy?: Office | null;
  lookback_label?: string;
  records_requested?: string;
  authorization_text?: string;
  release_to?: ReleaseTo;
  return_fax?: string;
  consent_wording?: string;
  expires_at?: string;
}

type Phase = 'loading' | 'invalid' | 'expired' | 'form' | 'submitting' | 'done' | 'error';

const labelCls = 'block text-xs font-medium text-gray-500 dark:text-gray-400';
// text-base (16px) on mobile prevents iOS Safari's zoom-on-focus; min-w-0 lets
// a paired input (e.g. State / ZIP) shrink inside its grid cell instead of
// overflowing onto its neighbour. ~100% of these forms are filled on a phone.
const inputCls =
  'mt-1 block w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-base text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:text-sm';
// Native <select> renders as a scroll-wheel picker on iOS — the easiest way for
// a patient to set their DOB on a phone, and (unlike <input type=date>) it sizes
// predictably so it never spills off the row. min-w-0 lets each shrink to a third.
const dobSelectCls =
  'block w-full min-w-0 rounded-lg border border-gray-300 bg-white px-2 py-2.5 text-base text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:text-sm';

const blankOffice = (): Office => ({ name: '', city_state: '', fax: '' });

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOB_CURRENT_YEAR = new Date().getFullYear();
// Newest first so recent birth years are a short scroll away; 1900 floor matches
// the recruiter-side DOB validation.
const DOB_YEARS = Array.from({ length: DOB_CURRENT_YEAR - 1900 + 1 }, (_, i) => DOB_CURRENT_YEAR - i);
const pad2 = (n: number | string) => String(n).padStart(2, '0');
/** Days in a given 1-12 month; falls back to 31 (no month) or a leap year (no year). */
function daysInMonth(year: number, month: number): number {
  if (!month) return 31;
  return new Date(year || 2000, month, 0).getDate();
}

/**
 * Month / Day / Year selects — on iOS these are native wheel pickers. Emits the
 * combined 'YYYY-MM-DD' to onChange only once all three are set (else ''), and
 * clamps the day when a month/year change would make it invalid (e.g. Feb 30).
 */
function DobWheels({ value, onChange }: { value?: string; onChange: (v: string) => void }) {
  const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  const [year, setYear] = useState(parsed ? parsed[1] : '');
  const [month, setMonth] = useState(parsed ? parsed[2] : '');
  const [day, setDay] = useState(parsed ? parsed[3] : '');

  const emit = (y: string, m: string, d: string) => onChange(y && m && d ? `${y}-${m}-${d}` : '');
  const clampDay = (y: string, m: string, d: string) => {
    if (!d) return d;
    const max = daysInMonth(Number(y), Number(m));
    return Number(d) > max ? pad2(max) : d;
  };

  const onMonth = (m: string) => { const d = clampDay(year, m, day); setMonth(m); setDay(d); emit(year, m, d); };
  const onYear = (y: string) => { const d = clampDay(y, month, day); setYear(y); setDay(d); emit(y, month, d); };
  const onDay = (d: string) => { setDay(d); emit(year, month, d); };

  const dayCount = daysInMonth(Number(year), Number(month));

  return (
    <div className="mt-1 grid grid-cols-3 gap-2">
      <select aria-label="Birth month" className={dobSelectCls} value={month} onChange={(e) => onMonth(e.target.value)}>
        <option value="">Month</option>
        {MONTHS.map((mm, i) => <option key={mm} value={pad2(i + 1)}>{mm}</option>)}
      </select>
      <select aria-label="Birth day" className={dobSelectCls} value={day} onChange={(e) => onDay(e.target.value)}>
        <option value="">Day</option>
        {Array.from({ length: dayCount }, (_, i) => i + 1).map((d) => <option key={d} value={pad2(d)}>{d}</option>)}
      </select>
      <select aria-label="Birth year" className={dobSelectCls} value={year} onChange={(e) => onYear(e.target.value)}>
        <option value="">Year</option>
        {DOB_YEARS.map((y) => <option key={y} value={String(y)}>{y}</option>)}
      </select>
    </div>
  );
}

export default function PublicSign() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [data, setData] = useState<SignState>({});

  // Editable patient + custodian fields.
  const [patient, setPatient] = useState<PatientFields>({});
  const [offices, setOffices] = useState<Office[]>([blankOffice()]);
  const [hospitalOn, setHospitalOn] = useState(false);
  const [hospital, setHospital] = useState<Office>(blankOffice());
  const [pharmacyOn, setPharmacyOn] = useState(false);
  const [pharmacy, setPharmacy] = useState<Office>(blankOffice());

  // Sign fields.
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasInitRef = useRef(false);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const load = useCallback(async () => {
    setPhase('loading');
    try {
      const res = await fetch(`${API_URL}/api/crm/public/sign/${token}`);
      if (res.status === 404) {
        const body = await res.json().catch(() => ({}));
        setPhase(body?.error === 'link_expired' ? 'expired' : 'invalid');
        return;
      }
      if (!res.ok) {
        setPhase('error');
        return;
      }
      const body: SignState = await res.json();
      setData(body);
      if (body.signed) {
        setPhase('done');
        return;
      }
      setPatient(body.prefill || {});
      setOffices(body.offices && body.offices.length ? body.offices.map((o) => ({ ...blankOffice(), ...o })) : [blankOffice()]);
      if (body.hospital && (body.hospital.name || body.hospital.fax)) {
        setHospital({ ...blankOffice(), ...body.hospital });
        setHospitalOn(true);
      }
      if (body.pharmacy && (body.pharmacy.name || body.pharmacy.fax)) {
        setPharmacy({ ...blankOffice(), ...body.pharmacy });
        setPharmacyOn(true);
      }
      setPhase('form');
    } catch {
      setPhase('error');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ---- Canvas ---------------------------------------------------------------
  // Initialized ONCE per entry into the form: resizing a canvas CLEARS it, and
  // this effect re-fires on the form -> submitting -> form flip after a failed
  // submit — re-initializing there would wipe the strokes.
  useEffect(() => {
    if (phase !== 'form' && phase !== 'submitting') {
      canvasInitRef.current = false;
      return;
    }
    if (canvasInitRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvasInitRef.current = true;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#111827';
    }
  }, [phase]);

  // Mid-sign rotation/resize: re-size the bitmap and redraw a scaled snapshot
  // so new strokes stay aligned and the drawn mark survives.
  useEffect(() => {
    if (phase !== 'form' && phase !== 'submitting') return;
    const onResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const newW = Math.round(rect.width * dpr);
      const newH = Math.round(rect.height * dpr);
      if (!newW || !newH || (newW === canvas.width && newH === canvas.height)) return;
      const snap = document.createElement('canvas');
      snap.width = canvas.width;
      snap.height = canvas.height;
      snap.getContext('2d')?.drawImage(canvas, 0, 0);
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#111827';
        if (snap.width > 0 && snap.height > 0) {
          ctx.drawImage(snap, 0, 0, rect.width, rect.height);
        }
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [phase]);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const ctx = e.currentTarget.getContext('2d');
    const last = lastPointRef.current;
    const next = pointFromEvent(e);
    if (ctx && last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(next.x, next.y);
      ctx.stroke();
      setHasStroke(true);
    }
    lastPointRef.current = next;
  };
  const onPointerUp = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setHasStroke(false);
  };
  const exportSignature = (): string | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const off = document.createElement('canvas');
    off.width = 600;
    off.height = 200;
    const ctx = off.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(canvas, 0, 0, 600, 200);
    return off.toDataURL('image/png');
  };

  // ---- Field helpers --------------------------------------------------------
  const setPatientField = (k: keyof PatientFields, v: string) =>
    setPatient((p) => ({ ...p, [k]: v }));
  const setOfficeField = (i: number, k: keyof Office, v: string) =>
    setOffices((list) => list.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));
  const addOffice = () => setOffices((list) => (list.length >= 10 ? list : [...list, blankOffice()]));
  const removeOffice = (i: number) => setOffices((list) => (list.length <= 1 ? list : list.filter((_, idx) => idx !== i)));

  // The patient names the office(s); the fax number is the recruiter's to add
  // later, so a named office is all that's required here.
  const completedOffices = offices.filter((o) => o.name.trim());
  const formReady =
    Boolean(patient.first_name?.trim()) && Boolean(patient.last_name?.trim()) && Boolean(patient.dob)
    && completedOffices.length > 0
    && Boolean(name.trim()) && hasStroke && consent;

  const submit = async () => {
    if (!formReady || phase === 'submitting') return;
    const png = exportSignature();
    if (!png) {
      setNotice('Could not capture the signature — please try again.');
      return;
    }
    setPhase('submitting');
    setNotice(null);
    const fields = {
      patient,
      // Only named offices are sent (a location-only row is meaningless); the
      // fax number is added by the recruiter at send time, not here.
      offices: offices
        .filter((o) => o.name.trim())
        .map((o) => ({ name: o.name.trim(), city_state: o.city_state.trim() })),
      hospital: hospitalOn && hospital.name.trim()
        ? { name: hospital.name.trim(), city_state: hospital.city_state.trim() }
        : null,
      pharmacy: pharmacyOn && pharmacy.name.trim()
        ? { name: pharmacy.name.trim(), city_state: pharmacy.city_state.trim() }
        : null,
    };
    try {
      const res = await fetch(`${API_URL}/api/crm/public/sign/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), signature_png: png, consent: true, fields }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setPhase('done');
        return;
      }
      if (res.status === 404) {
        setPhase(body?.error === 'link_expired' ? 'expired' : 'invalid');
        return;
      }
      setPhase('form');
      const map: Record<string, string> = {
        office_required: 'Please add at least one doctor’s office name.',
        signature_too_large: 'That signature could not be processed — tap Clear and sign again.',
        invalid_signature: 'Please draw your signature in the box.',
        name_required: 'Please type your full legal name.',
        consent_required: 'Please check the consent box to continue.',
      };
      setNotice(map[body?.error] || 'Something went wrong submitting the release. Please try again.');
    } catch {
      setPhase('form');
      setNotice('Connection problem — please check your signal and try again.');
    }
  };

  const greetingName = data.first_name ? `Hi ${data.first_name}!` : 'Hi there!';

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 dark:bg-gray-900">
      <div className="mx-auto w-full max-w-md">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-xl font-bold text-white">W</div>
          <h1 className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-100">West Clinical Research</h1>
        </div>

        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          {phase === 'loading' && (
            <div className="flex items-center justify-center py-12">
              <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-primary-600" />
            </div>
          )}

          {(phase === 'invalid' || phase === 'expired') && (
            <div className="py-6 text-center">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {phase === 'expired' ? 'This link has expired' : 'This link is no longer valid'}
              </h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No problem — just reply to our text message and we'll send you a fresh link, or we'll give you a call.
              </p>
            </div>
          )}

          {phase === 'error' && (
            <div className="py-6 text-center">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">This page is temporarily unavailable</h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Please try again in a few minutes.</p>
              <button
                onClick={() => load()}
                className="mt-4 min-h-[44px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
              >
                Try again
              </button>
            </div>
          )}

          {phase === 'done' && (
            <div className="py-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-2xl dark:bg-green-900/30">✓</div>
              <h2 className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">All signed — thank you!</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                We've sent the release to your doctor's office. We'll be in touch when we receive your records.
              </p>
            </div>
          )}

          {(phase === 'form' || phase === 'submitting') && (
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{greetingName}</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Please review your information, tell us which doctor's office(s) to request records from, and sign below.
              </p>

              {notice && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  {notice}
                </div>
              )}

              {/* ---- Your information ---- */}
              <h3 className="mt-5 text-sm font-semibold text-gray-900 dark:text-gray-100">Your information</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">We've filled in what we have — please correct anything that's wrong.</p>
              {/* Mobile-first: DOB + Phone are full-width rows (the native date
                  picker won't shrink in a 2-col cell -> it overlapped Phone).
                  Only the short paired fields share a row, each min-w-0. */}
              <div className="mt-2 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label className={labelCls}>First name</label>
                    <input className={inputCls} value={patient.first_name || ''} onChange={(e) => setPatientField('first_name', e.target.value.slice(0, 100))} autoComplete="given-name" />
                  </div>
                  <div className="min-w-0">
                    <label className={labelCls}>Last name</label>
                    <input className={inputCls} value={patient.last_name || ''} onChange={(e) => setPatientField('last_name', e.target.value.slice(0, 100))} autoComplete="family-name" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Date of birth</label>
                  <DobWheels value={patient.dob} onChange={(v) => setPatientField('dob', v)} />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input type="tel" className={inputCls} value={patient.phone || ''} onChange={(e) => setPatientField('phone', e.target.value.slice(0, 40))} autoComplete="tel" />
                </div>
                <div>
                  <label className={labelCls}>Street address</label>
                  <input className={inputCls} value={patient.address || ''} onChange={(e) => setPatientField('address', e.target.value.slice(0, 200))} autoComplete="street-address" />
                </div>
                <div>
                  <label className={labelCls}>City</label>
                  <input className={inputCls} value={patient.city || ''} onChange={(e) => setPatientField('city', e.target.value.slice(0, 100))} autoComplete="address-level2" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label className={labelCls}>State</label>
                    <input className={inputCls} value={patient.state || ''} onChange={(e) => setPatientField('state', e.target.value.slice(0, 60))} autoComplete="address-level1" />
                  </div>
                  <div className="min-w-0">
                    <label className={labelCls}>ZIP</label>
                    <input className={inputCls} value={patient.zip || ''} onChange={(e) => setPatientField('zip', e.target.value.slice(0, 20))} autoComplete="postal-code" inputMode="numeric" />
                  </div>
                </div>
              </div>

              {/* ---- Doctor's office(s) ---- */}
              <h3 className="mt-6 text-sm font-semibold text-gray-900 dark:text-gray-100">Doctor's office(s)</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Where should we request your records from? Just add each office's name — you don't need the fax number, our team will take care of that.</p>
              <div className="mt-2 space-y-3">
                {offices.map((o, i) => (
                  <div key={i} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Office {i + 1}</span>
                      {offices.length > 1 && (
                        <button onClick={() => removeOffice(i)} className="inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500 sm:min-h-0" aria-label={`Remove office ${i + 1}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Remove
                        </button>
                      )}
                    </div>
                    <div className="mt-1.5 space-y-2.5">
                      <div>
                        <label className={labelCls}>Doctor's name</label>
                        <input className={inputCls} value={o.name} onChange={(e) => setOfficeField(i, 'name', e.target.value.slice(0, 200))} placeholder="e.g. Dr. Jane Smith" />
                      </div>
                      <div>
                        <label className={labelCls}>Location <span className="font-normal">(city, state — optional)</span></label>
                        <input className={inputCls} value={o.city_state} onChange={(e) => setOfficeField(i, 'city_state', e.target.value.slice(0, 120))} placeholder="e.g. Raleigh, NC" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {offices.length < 10 && (
                <button onClick={addOffice} className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 text-xs font-medium text-primary-700 hover:underline dark:text-primary-400 sm:min-h-0">
                  <Plus className="h-3.5 w-3.5" /> Add another office
                </button>
              )}

              {/* ---- Local hospital (optional) ---- */}
              <h3 className="mt-6 text-sm font-semibold text-gray-900 dark:text-gray-100">Local hospital <span className="font-normal text-gray-400">(optional)</span></h3>
              {!hospitalOn ? (
                <button onClick={() => setHospitalOn(true)} className="mt-1 inline-flex min-h-[44px] items-center gap-1.5 text-xs font-medium text-primary-700 hover:underline dark:text-primary-400 sm:min-h-0">
                  <Plus className="h-3.5 w-3.5" /> Add a local hospital
                </button>
              ) : (
                <div className="mt-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="flex items-center justify-end">
                    <button onClick={() => { setHospitalOn(false); setHospital(blankOffice()); }} className="inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500 sm:min-h-0">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <label className={labelCls}>Hospital name</label>
                      <input className={inputCls} value={hospital.name} onChange={(e) => setHospital((h) => ({ ...h, name: e.target.value.slice(0, 200) }))} placeholder="e.g. Raleigh General Hospital" />
                    </div>
                    <div>
                      <label className={labelCls}>Location <span className="font-normal">(city, state — optional)</span></label>
                      <input className={inputCls} value={hospital.city_state} onChange={(e) => setHospital((h) => ({ ...h, city_state: e.target.value.slice(0, 120) }))} />
                    </div>
                  </div>
                </div>
              )}

              {/* ---- Pharmacy (optional) ---- */}
              <h3 className="mt-6 text-sm font-semibold text-gray-900 dark:text-gray-100">Pharmacy <span className="font-normal text-gray-400">(optional)</span></h3>
              {!pharmacyOn ? (
                <button onClick={() => setPharmacyOn(true)} className="mt-1 inline-flex min-h-[44px] items-center gap-1.5 text-xs font-medium text-primary-700 hover:underline dark:text-primary-400 sm:min-h-0">
                  <Plus className="h-3.5 w-3.5" /> Add a pharmacy
                </button>
              ) : (
                <div className="mt-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="flex items-center justify-end">
                    <button onClick={() => { setPharmacyOn(false); setPharmacy(blankOffice()); }} className="inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500 sm:min-h-0">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <label className={labelCls}>Pharmacy name</label>
                      <input className={inputCls} value={pharmacy.name} onChange={(e) => setPharmacy((p) => ({ ...p, name: e.target.value.slice(0, 200) }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Location <span className="font-normal">(city, state — optional)</span></label>
                      <input className={inputCls} value={pharmacy.city_state} onChange={(e) => setPharmacy((p) => ({ ...p, city_state: e.target.value.slice(0, 120) }))} />
                    </div>
                  </div>
                </div>
              )}

              {/* ---- What we're requesting (read-only) ---- */}
              <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
                <p><span className="font-medium text-gray-700 dark:text-gray-200">Records requested:</span> {data.records_requested}</p>
                {data.lookback_label && <p className="mt-1"><span className="font-medium text-gray-700 dark:text-gray-200">Dates of service:</span> {data.lookback_label}</p>}
                {data.return_fax && (
                  <p className="mt-1">
                    Records are faxed back to {data.release_to?.name || 'West Clinical Research'} at <span className="font-medium text-gray-700 dark:text-gray-200">{data.return_fax}</span>.
                  </p>
                )}
              </div>

              {/* ---- Authorization text (read-only) ---- */}
              {data.authorization_text && (
                <div className="mt-3">
                  <span className={labelCls}>Authorization</span>
                  <div className="mt-1 max-h-44 overflow-y-auto whitespace-pre-line rounded-lg border border-gray-200 bg-white p-3 text-[11px] leading-relaxed text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {data.authorization_text}
                  </div>
                </div>
              )}

              {/* ---- Sign ---- */}
              <h3 className="mt-6 text-sm font-semibold text-gray-900 dark:text-gray-100">Sign</h3>
              <label className="mt-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Your full legal name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 200))}
                autoComplete="name"
                placeholder="e.g. Jane A. Smith"
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />

              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Draw your signature</span>
                <button onClick={clearCanvas} className="min-h-[44px] text-xs font-medium text-primary-700 hover:underline dark:text-primary-400 sm:min-h-0">Clear</button>
              </div>
              <div className="relative mt-1">
                <canvas
                  ref={canvasRef}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  style={{ touchAction: 'none', width: '100%', height: 160, display: 'block' }}
                  className="relative z-10 rounded-lg border border-gray-300 bg-transparent dark:border-gray-600"
                  aria-label="Signature drawing area"
                />
                {/* White backing + baseline rule live BEHIND the canvas so the
                    exported PNG carries transparent strokes only. */}
                <div aria-hidden="true" className="absolute inset-0 rounded-lg bg-white">
                  <div className="absolute inset-x-3 bottom-6 border-t border-gray-300" />
                </div>
              </div>

              {/* Big, obvious tap target — the whole bordered row toggles the
                  box (native label behavior); the checkbox itself is enlarged
                  for thumbs on mobile. */}
              <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 active:bg-gray-50 dark:border-gray-700 dark:active:bg-gray-700/40">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-6 w-6 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">{data.consent_wording}</span>
              </label>

              <button
                onClick={submit}
                disabled={phase === 'submitting' || !formReady}
                className="mt-5 w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {phase === 'submitting' ? 'Signing…' : 'Sign release'}
              </button>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-gray-400">
          Your information is sent securely to our team. Questions? Reply to our text message any time.
        </p>
      </div>
    </div>
  );
}
